import { createAction } from "@reduxjs/toolkit"

import { Environment } from "~/shared/api/environment"
import { List } from "~/shared/api/lists"
import {
  DenormalizedTargetAttribute,
  DenormalizedTemplate,
  TargetAttribute,
  Template,
} from "~/shared/api/templates"
import { DenormalizedWorkspace, Workspace } from "~/shared/api/workspaces"

export interface NormalizedEntities {
  templates: { [id: number]: Template }
  targetAttributes: { [id: number]: TargetAttribute }
  workspaces: { [id: number]: Workspace }
  lists: { [id: number]: List }
  environments: { [id: number]: Environment }
}

interface NormalizedResult<R> {
  result: R
  entities: NormalizedEntities
}

// This action is used to initialize multiple slices of the global store at once
// given the result of calling normalize on a central data structure.
export const partialInitSchema =
  createAction<Partial<NormalizedEntities>>("schema/partial-init")

function emptyEntities(): NormalizedEntities {
  return {
    templates: {},
    targetAttributes: {},
    workspaces: {},
    lists: {},
    environments: {},
  }
}

// These are typed normalize functions that flatten nested API responses
// into lookup maps keyed by id, matching the shape previously produced
// by normalizr.

function normalizeTemplateInto(
  template: DenormalizedTemplate,
  entities: NormalizedEntities,
): number {
  const { targetAttributes, ...rest } = template
  const targetAttributeIds = (targetAttributes ?? []).map((ta) => {
    entities.targetAttributes[ta.id] = ta
    return ta.id
  })
  entities.templates[template.id] = { ...rest, targetAttributeIds }
  return template.id
}

export function normalizeTemplate(
  template: DenormalizedTemplate,
): NormalizedResult<number> {
  const entities = emptyEntities()
  return { result: normalizeTemplateInto(template, entities), entities }
}

export function normalizeTemplates(
  templates: DenormalizedTemplate[],
): NormalizedResult<number[]> {
  const entities = emptyEntities()
  const result = templates.map((t) => normalizeTemplateInto(t, entities))
  return { result, entities }
}

export function normalizeTargetAttribute(
  targetAttribute: DenormalizedTargetAttribute,
): NormalizedResult<number> {
  const entities = emptyEntities()
  entities.targetAttributes[targetAttribute.id] = targetAttribute

  return { result: targetAttribute.id, entities }
}

function normalizeWorkspaceInto(
  workspace: DenormalizedWorkspace,
  entities: NormalizedEntities,
): number {
  const { lists, ...rest } = workspace
  const listIds = (lists ?? []).map((list) => {
    entities.lists[list.id] = list
    return list.id
  })
  entities.workspaces[workspace.id] = { ...rest, listIds }
  return workspace.id
}

export function normalizeWorkspace(
  workspace: DenormalizedWorkspace,
): NormalizedResult<number> {
  const entities = emptyEntities()
  return { result: normalizeWorkspaceInto(workspace, entities), entities }
}

export function normalizeWorkspaces(
  workspaces: DenormalizedWorkspace[],
): NormalizedResult<number[]> {
  const entities = emptyEntities()
  const result = workspaces.map((w) => normalizeWorkspaceInto(w, entities))
  return { result, entities }
}

export function normalizeEnvironment(environment: Environment): NormalizedResult<number> {
  const entities = emptyEntities()
  entities.environments[environment.id] = environment

  return { result: environment.id, entities }
}

export function normalizeEnvironments(
  environments: Environment[],
): NormalizedResult<number[]> {
  const entities = emptyEntities()
  const result: number[] = []

  for (const environment of environments) {
    entities.environments[environment.id] = environment
    result.push(environment.id)
  }

  return { result, entities }
}
